/**
 * TTS Service — Web Speech API 래퍼 (한국어 음성)
 * 프리로딩 및 재생 상태 관리 지원
 */
export class TTS {
    private synth: SpeechSynthesis | null = null;
    private _speaking: boolean = false;
    private preloadQueue: string[] = [];
    private preloading: boolean = false;

    constructor() {
        this.synth = typeof window !== 'undefined' ? window.speechSynthesis : null;
    }

    public get isSupported(): boolean {
        return !!this.synth;
    }

    public get isSpeaking(): boolean {
        return this._speaking;
    }

    /**
     * 한국어 텍스트를 음성으로 재생
     * @param text 읽을 텍스트
     * @param rate 속도 (0.1 ~ 10, 기본 0.8)
     */
    public speak(text: string, rate: number = 0.8): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.synth) {
                reject(new Error('TTS not supported'));
                return;
            }

            this.synth.cancel();
            this.preloading = false;
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = 'ko-KR';
            utterance.rate = rate;

            utterance.onstart = () => { this._speaking = true; };
            utterance.onend = () => { this._speaking = false; resolve(); };
            utterance.onerror = (e) => { this._speaking = false; reject(e); };

            this.synth.speak(utterance);
        });
    }

    /**
     * 다음 단어들을 미리 발음 합성 큐에 넣어 워밍업
     * Web Speech API는 실제 캐싱을 지원하지 않으나,
     * 무음 utterance를 통해 voice 엔진 초기화를 앞당긴다.
     * @param texts 프리로드할 텍스트 배열
     */
    public preload(texts: string[]): void {
        if (!this.synth || this._speaking) return;
        this.preloadQueue = [...texts];
        this.processPreloadQueue();
    }

    private processPreloadQueue(): void {
        if (!this.synth || this.preloadQueue.length === 0 || this._speaking) {
            this.preloading = false;
            return;
        }

        this.preloading = true;
        const text = this.preloadQueue.shift()!;
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'ko-KR';
        utterance.rate = 0.8;
        utterance.volume = 0; // 무음으로 워밍업

        utterance.onend = () => {
            if (!this._speaking) {
                this.processPreloadQueue();
            } else {
                this.preloading = false;
            }
        };
        utterance.onerror = () => {
            this.preloading = false;
        };

        this.synth.speak(utterance);
    }

    /** 재생 중지 */
    public stop(): void {
        if (this.synth) {
            this.synth.cancel();
            this._speaking = false;
            this.preloading = false;
            this.preloadQueue = [];
        }
    }
}

export default TTS;
